import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import LoadMoreButton from '@atoms/LoadMoreButton/LoadMoreButton';
import IExternalVideo from '@models/IExternalVideo';
import chunkArray from '@utils/helpers/chunkArray';
import { motion } from 'framer-motion';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

interface Props {
  readonly activeSourceIndex: number;
  readonly loadMoreText: string;
  readonly setActiveSource?: Dispatch<SetStateAction<string>>;
  readonly setActiveSourceIndex?: Dispatch<SetStateAction<number>>;
  readonly setIsPlaying?: Dispatch<SetStateAction<boolean>>;
  readonly videoPlaylist: IExternalVideo[];
}

const MobileVideoList = ({
  videoPlaylist = [],
  setActiveSource,
  setActiveSourceIndex,
  activeSourceIndex = 0,
  setIsPlaying,
  loadMoreText = '',
}: Props) => {
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const [chunkedArray, setChunkedArray] = useState<IExternalVideo[][]>([]);
  const [visibleVideos, setVisibleVideos] = useState<IExternalVideo[]>([]);

  useEffect(() => {
    if (videoPlaylist?.length > 0) {
      const chunkedIntoThree = chunkArray(videoPlaylist, 4);
      setVisibleVideos(chunkedIntoThree[0]!);
      setChunkedArray(chunkedIntoThree);
    }
  }, [videoPlaylist]);

  return (
    <div className="mobile h-auto pt-5">
      <motion.div
        className="max-w-md"
        animate={{
          height: '100%',
        }}
        data-testid="CT-10-B-Video-Embed-Playlist-External-Video-Mobile-Video-List"
      >
        {videoPlaylist[activeSourceIndex]?.title && (
          <h3
            className="h5 pb-5 text-left"
            data-testid="CT-10-B-Video-Embed-Playlist-External-Video-Mobile-Video-List-Active-Title"
          >
            {videoPlaylist[activeSourceIndex]!.title}
          </h3>
        )}
        <div className="mb-8 flex flex-col gap-5">
          {visibleVideos.length > 0 &&
            visibleVideos?.map(
              ({ coverImage, videoId, title, subTitle }, index) => (
                <div
                  key={`mobile-video-${title}-${subTitle}`}
                  className="flex flex-row"
                >
                  <button
                    type="button"
                    className="w-1/2 sm:w-1/3"
                    onClick={() => {
                      setActiveSource?.(videoId!);
                      setActiveSourceIndex?.(index);
                      setIsPlaying?.(true);
                    }}
                    data-testid={`CT-10-B-Video-Embed-Playlist-External-Video-Mobile-Video-List-Item-${index}-Play-Button`}
                  >
                    <span className="relative flex items-center justify-center">
                      <Image
                        image={coverImage}
                        data-testid={`CT-10-B-Video-Embed-Playlist-External-Video-Mobile-Video-List-Item-${index}-Cover-Image`}
                      />
                      <Icon
                        name="play_icon_circle"
                        size="27px"
                        className="absolute"
                      />
                    </span>
                  </button>
                  <div className="flex w-1/2 items-center pr-1 pl-4 sm:w-2/3">
                    {title && (
                      <p
                        className="body-four line-clamp-4 text-left text-grey-450 dark:text-grey-400"
                        data-testid={`CT-10-B-Video-Embed-Playlist-External-Video-Desktop-Video-List-Item-${index}-Subtitle`}
                      >
                        <span
                          className="body-four text-black-100 dark:text-white"
                          data-testid={`CT-10-B-Video-Embed-Playlist-External-Video-Desktop-Video-List-Item-${index}-Title`}
                        >
                          {title}{' '}
                        </span>
                        {subTitle}
                      </p>
                    )}
                  </div>
                </div>
              )
            )}
        </div>
      </motion.div>

      {chunkedArray && chunkedArray[activeGroupIndex + 1] && (
        <div className="flex justify-center">
          <LoadMoreButton
            loadMoreLabel={loadMoreText}
            onClick={() => {
              if (chunkedArray && chunkedArray[activeGroupIndex + 1]) {
                const nextChunk = chunkedArray.filter(
                  (_, index) => index === activeGroupIndex + 1
                );

                setVisibleVideos((oldState) => [...oldState, ...nextChunk[0]!]);
                setActiveGroupIndex((oldState) => oldState + 1);
              }
            }}
            dataTestId="CT-10-B-Video-Embed-Playlist-External-Video-Desktop-Video-List-Item-Load-More"
          />
        </div>
      )}
    </div>
  );
};

export default MobileVideoList;
